/**
 * Notification API Service
 * Handles all notification-related API calls
 */

import axios from "@/lib/axios";
import type {
    Notification,
    NotificationList,
    NotificationQuery,
    UnreadCount,
    NotificationPreference,
    NotificationPreferenceUpdate,
    NotificationDevice,
    DeviceRegistration,
    BulkMarkReadRequest,
} from "@/types/notification";

class NotificationService {
    /**
     * List notifications with filters and pagination
     */
    async listNotifications(query?: NotificationQuery): Promise<NotificationList> {
        const params = new URLSearchParams();

        if (query?.category) params.append("category", query.category);
        if (query?.is_read !== undefined) params.append("is_read", String(query.is_read));
        if (query?.priority) params.append("priority", query.priority);
        if (query?.type) params.append("type", query.type);
        if (query?.page) params.append("page", String(query.page));
        if (query?.limit) params.append("limit", String(query.limit));
        if (query?.sort) params.append("sort", query.sort);

        const response = await axios.get(`/notifications?${params.toString()}`);
        return response.data;
    }

    /**
     * Create a new notification
     */
    async createNotification(notification: any): Promise<Notification> {
        const response = await axios.post("/notifications", notification);
        return response.data;
    }

    /**
     * Get unread notification count
     */
    async getUnreadCount(): Promise<UnreadCount> {
        const response = await axios.get("/notifications/unread-count");
        return response.data;
    }

    /**
     * Get a specific notification by ID
     */
    async getNotification(notificationId: string): Promise<Notification> {
        const response = await axios.get(`/notifications/${notificationId}`);
        return response.data;
    }

    /**
     * Mark a notification as read
     */
    async markAsRead(notificationId: string): Promise<Notification> {
        const response = await axios.patch(`/notifications/${notificationId}/read`);
        return response.data;
    }

    /**
     * Mark a notification as clicked
     */
    async markAsClicked(notificationId: string): Promise<Notification> {
        const response = await axios.patch(`/notifications/${notificationId}/clicked`);
        return response.data;
    }

    /**
     * Bulk mark notifications as read
     */
    async bulkMarkAsRead(request: BulkMarkReadRequest): Promise<{ marked_read: number }> {
        const response = await axios.post("/notifications/mark-read", request);
        return response.data;
    }

    /**
     * Delete a notification
     */
    async deleteNotification(notificationId: string): Promise<void> {
        await axios.delete(`/notifications/${notificationId}`);
    }

    /**
     * Get user notification preferences
     */
    async getPreferences(): Promise<NotificationPreference> {
        const response = await axios.get("/notification-preferences");
        return response.data;
    }

    /**
     * Update user notification preferences
     */
    async updatePreferences(
        preferences: NotificationPreferenceUpdate
    ): Promise<NotificationPreference> {
        const response = await axios.patch("/notification-preferences", preferences);
        return response.data;
    }

    /**
     * List user devices
     */
    async listDevices(activeOnly: boolean = true): Promise<NotificationDevice[]> {
        const response = await axios.get(`/notification-devices?active_only=${activeOnly}`);
        return response.data;
    }

    /**
     * Register a device for push notifications
     */
    async registerDevice(device: DeviceRegistration): Promise<NotificationDevice> {
        const response = await axios.post("/notification-devices", device);
        return response.data;
    }

    /**
     * Deactivate a device
     */
    async deactivateDevice(deviceId: number): Promise<{ status: string }> {
        const response = await axios.patch(`/notification-devices/${deviceId}/deactivate`);
        return response.data;
    }

    /**
     * Delete a device
     */
    async deleteDevice(deviceId: number): Promise<void> {
        await axios.delete(`/notification-devices/${deviceId}`);
    }
}

export default new NotificationService();
